import { stratusProdFallbackUrl, stratusFileProxyUrl } from './stratus-url.util';

/**
 * A single image source with optional ordering hints. Mirrors the shape of
 * `PropertyImage` but is intentionally decoupled so non-property surfaces can
 * reuse the same fallback-chain logic.
 */
export interface ImageCandidateInput {
    url?: string | null;
    thumb?: string | null;
    isMain?: boolean | null;
    order?: number | null;
}

/**
 * Drop URLs that can never render: empty, the `pending.local` placeholder the
 * backend emits before an upload finishes, or non-absolute paths.
 */
export function normalizeImageUrl(url?: string | null): string | null {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (lower.includes('pending.local') || (!lower.startsWith('http://') && !lower.startsWith('https://'))) {
        return null;
    }
    return url;
}

/**
 * Expand a primary image + an array of image candidates into an ordered,
 * de-duplicated list of URLs to try in sequence. Main images first, then by
 * `order`; each URL is followed by its Stratus prod-fallback and file-proxy
 * variants so a transient dev/prod bucket miss falls through to a working URL
 * instead of showing a broken image.
 */
export function buildImageCandidates(primary?: string | null, images?: ImageCandidateInput[] | null): string[] {
    const ordered = [...(images || [])].sort((a, b) => {
        const mainDelta = Number(Boolean(b.isMain)) - Number(Boolean(a.isMain));
        if (mainDelta !== 0) return mainDelta;
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return 0;
    });

    const base = [primary, ...ordered.flatMap((image) => [image.url, image.thumb])]
        .map((value) => normalizeImageUrl(value))
        .filter(Boolean) as string[];

    const expanded = base.flatMap((value) => {
        const chain: string[] = [value];
        const prodFallback = stratusProdFallbackUrl(value);
        if (prodFallback && prodFallback !== value) chain.push(prodFallback);
        const proxyFallback = stratusFileProxyUrl(value);
        if (proxyFallback && proxyFallback !== value && !chain.includes(proxyFallback)) chain.push(proxyFallback);
        return chain;
    });

    return Array.from(new Set(expanded));
}
